import { Card } from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import { BroadcastScheduleProgramBodySegmentToolkitNavigation } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitNavigation';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useDeleteClipOcr } from '@/features/clips/api/delete-clip-ocr';
import { useClipTextOnScreenPermissions } from '@/features/clips/hooks/useClipTextOnScreenPermissions';
import { TClipOcr } from '@/features/clips/types';
import { convertISOToSeconds } from '@/utils/hh-mm-ss-into-number';

import { ClipRotuloForm } from './components/ClipRotuloForm';
import { ClipRotuloTable } from './components/ClipRotuloTable';

export default function ClipRotulosComponent() {
  const {
    formBox,
    activeClip,
    videoControlRef,
    rotuloClipData,
    setActiveClip,
    setRotuloClipData,
    handleTooltikClick,
    setFormBox,
    setClipItems,
  } = useBroadcastScheduleProgram();
  const { mutateAsync: deleteOcrClip } = useDeleteClipOcr({});
  const { t } = useTranslation();

  const handlePreviewBox = (box: TClipOcr) => {
    if (!activeClip) return;

    const seconds = convertISOToSeconds(box.start_datetime, new Date(activeClip.start_datetime));

    videoControlRef.current?.seekToSecond(seconds || 0);
  };

  const { canCreate, canUpdate, canDelete } = useClipTextOnScreenPermissions();

  const handleEdit = useCallback(
    (item: TClipOcr) => {
      if (!canUpdate) return;
      setFormBox('rotulo');
      setRotuloClipData(item);
    },
    [setFormBox, canUpdate]
  );

  const handleCreate = useCallback(() => {
    if (!canCreate) return;
    setFormBox('rotulo');
  }, [setFormBox, canCreate]);

  const handleDelete = useCallback(
    (item: TClipOcr) => {
      if (!canDelete) return;
      deleteOcrClip(item.id || '').then(() => {
        setActiveClip(prev => {
          if (prev) {
            return {
              ...prev,
              clip_ocrs: prev.clip_ocrs?.filter(logo => logo.id !== item.id),
            };
          }
          return prev;
        });
        setClipItems(prev => {
          if (!prev) return prev;
          return prev.map(clip => {
            if (clip.id === activeClip?.id) {
              return { ...clip, clip_ocrs: clip.clip_ocrs?.filter(ocr => ocr.id !== item.id) };
            }
            return clip;
          });
        });
      });
    },
    [canDelete]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'i') {
        event.preventDefault();
        handleCreate();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreate]);

  return (
    <Card
      classes={{
        rootSection: clsx('w-full flex-auto order-3 lg:order-2'),
        container: 'py-size-4',
      }}
    >
      <div className="flex w-full flex-col gap-2">
        {formBox !== 'rotulo' ? (
          <>
            <GeneralHeader
              name={t('Rótulos')}
              buttonName={canCreate ? t('Añadir') : undefined}
              onClick={handleCreate}
              onClose={() => handleTooltikClick(ESegmentToolkit.ROTULOS)}
            />
            <ClipRotuloTable
              items={activeClip?.clip_ocrs || []}
              onPreview={handlePreviewBox}
              onUpdate={handleEdit}
              onDelete={handleDelete}
            />
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <ClipRotuloForm />
            {rotuloClipData && (
              <BroadcastScheduleProgramBodySegmentToolkitNavigation
                items={activeClip?.clip_ocrs || []}
                activeItem={rotuloClipData}
                onChangeData={setRotuloClipData}
              />
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
